package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.PosIntegration
import com.evoapps.evoshift.domain.model.PosProvider
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.PosIntegrationRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.salesimport.SalesSheet
import com.evoapps.evoshift.domain.salesimport.SalesSheetParser
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.finance.ImportDailySalesUseCase
import com.evoapps.evoshift.domain.usecase.finance.SalesImportPlan
import com.evoapps.evoshift.domain.usecase.finance.SalesImportResult
import com.evoapps.evoshift.salesimport.IncomingSalesFiles
import com.evoapps.evoshift.salesimport.SalesFileHandoff
import com.evoapps.evoshift.salesimport.SalesFileInbox
import com.evoapps.evoshift.salesimport.onlyMonth
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.sales_import_applied
import evoshift.composeapp.generated.resources.sales_import_file_unreadable
import evoshift.composeapp.generated.resources.pos_connected
import evoshift.composeapp.generated.resources.pos_disconnected
import evoshift.composeapp.generated.resources.pos_synced
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.datetime.YearMonth

/** A file the person picked: its name (for the preview) and bytes. */
class PickedSalesFile(val name: String, val bytes: ByteArray)

data class SalesImportUiState(
    val workplaceId: String? = null,
    val currency: String = "PLN",
    /** Seeing the screen needs [Gate.MONEY]; importing and the till need [Gate.MONEY_EDITING]. */
    val capabilities: Set<Capability> = emptySet(),
    // --- The file ---
    val fileName: String? = null,
    val isReading: Boolean = false,
    val sheet: SalesSheet? = null,
    val plan: SalesImportPlan? = null,
    val isApplying: Boolean = false,
    /** SH-161 F4: a file opened with the app for one month only (its other days left out); null: all. */
    val month: YearMonth? = null,
    val lastResult: SalesImportResult? = null,
    // --- The till ---
    val integration: PosIntegration? = null,
    val integrationLoaded: Boolean = false,
    val isConnecting: Boolean = false,
    val isSyncing: Boolean = false,
    val isDisconnecting: Boolean = false,
    val message: UiText? = null
) {
    val canEdit: Boolean get() = capabilities.shows(Gate.MONEY_EDITING)
}

/**
 * SH-123: Sales import (More → Sales import), owners and financial managers: a CSV/XLSX file of
 * daily net sales is read and previewed (what each day becomes; figures typed by hand win), then
 * imported into the daily records; and the till connection (SumUp: the API key goes once to the
 * connectPos function, which keeps it in Secret Manager), "Import now" and disconnecting.
 */
class SalesImportViewModel(
    private val importDailySales: ImportDailySalesUseCase,
    private val posIntegrations: PosIntegrationRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** Where files are read (off the main thread; tests read them in place). */
    private val parsing: CoroutineDispatcher = Dispatchers.Default,
    /** SH-161 F4: files opened with the app (Open with, Share), handed to this workplace's import. */
    private val inbox: SalesFileInbox = IncomingSalesFiles
) : ViewModel() {

    private val _uiState = MutableStateFlow(SalesImportUiState())
    val uiState: StateFlow<SalesImportUiState> = _uiState.asStateFlow()

    init {
        watchInbox()
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                if (workplaceId == null) return@collectLatest
                val user = getCurrentUser().first() ?: return@collectLatest
                val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
                val currency = workplaceRepository.getWorkplace(workplaceId).getOrNull()?.settings?.currency?.takeIf { it.isNotBlank() } ?: "PLN"
                _uiState.update { state ->
                    val switched = state.workplaceId != workplaceId
                    if (switched) SalesImportUiState(workplaceId = workplaceId, currency = currency, capabilities = capabilities)
                    else state.copy(capabilities = capabilities, currency = currency)
                }
                if (!capabilities.shows(Gate.MONEY)) {
                    _uiState.update { it.copy(integrationLoaded = true) }
                    return@collectLatest
                }
                // SH-161 F4: a file opened with the app for this workplace, read as a picked one.
                inbox.take(workplaceId)?.let(::onIncoming)
                posIntegrations.observe(workplaceId)
                    .catch { e -> _uiState.update { it.copy(integrationLoaded = true, message = e.toUiText()) } }
                    .collect { integration -> _uiState.update { it.copy(integration = integration, integrationLoaded = true) } }
            }
        }
    }

    /**
     * A file the system opened the app with, once its workplace is this one (also while the
     * screen is already open): read as a picked file, with only the month the person chose.
     */
    private fun watchInbox() = viewModelScope.launch {
        inbox.handoff.collect { handoff ->
            val workplaceId = _uiState.value.workplaceId ?: return@collect
            if (handoff?.workplaceId == workplaceId) inbox.take(workplaceId)?.let(::onIncoming)
        }
    }

    private fun onIncoming(handoff: SalesFileHandoff) {
        val bytes = handoff.file.bytes
        onFilePicked(bytes?.let { PickedSalesFile(handoff.file.name, it) }, handoff.month)
    }

    /**
     * A file from the picker (null: it could not be read): read, then planned against the stored
     * days. [month]: only that month's days (SH-161 F4, a file opened with the app).
     */
    fun onFilePicked(file: PickedSalesFile?, month: YearMonth? = null) {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (!state.canEdit) return
        if (file == null) {
            _uiState.update { it.copy(message = UiText.Resource(Res.string.sales_import_file_unreadable)) }
            return
        }
        _uiState.update { it.copy(fileName = file.name, isReading = true, sheet = null, plan = null, lastResult = null, month = month) }
        viewModelScope.launch {
            runCatching {
                val sheet = withContext(parsing) { SalesSheetParser.parse(file.bytes).onlyMonth(month) }
                sheet to importDailySales.plan(workplaceId, sheet.days).getOrThrow()
            }.fold(
                onSuccess = { (sheet, plan) -> _uiState.update { it.copy(isReading = false, sheet = sheet, plan = plan) } },
                onFailure = { e -> _uiState.update { it.copy(isReading = false, fileName = null, month = null, message = e.toUiText()) } }
            )
        }
    }

    /** Writes the previewed days (planned again against the data as it is now: manual figures win). */
    fun applyImport() {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        val sheet = state.sheet ?: return
        if (!state.canEdit || state.isApplying || (state.plan?.toWrite ?: 0) == 0) return
        _uiState.update { it.copy(isApplying = true) }
        viewModelScope.launch {
            importDailySales.apply(workplaceId, sheet.days).fold(
                onSuccess = { result ->
                    _uiState.update {
                        it.copy(
                            isApplying = false, sheet = null, plan = null, fileName = null, month = null, lastResult = result,
                            message = UiText.Resource(Res.string.sales_import_applied, result.written)
                        )
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isApplying = false, message = e.toUiText()) } }
            )
        }
    }

    fun clearFile() = _uiState.update { it.copy(fileName = null, sheet = null, plan = null, isReading = false, month = null) }

    /** Connects the till with [apiKey] (sent once to the function, not kept here) and the VAT rate. */
    fun connect(apiKey: String, vatRatePercent: Double) {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (!state.canEdit || state.isConnecting) return
        _uiState.update { it.copy(isConnecting = true) }
        viewModelScope.launch {
            posIntegrations.connect(workplaceId, PosProvider.SUMUP, apiKey, vatRatePercent).fold(
                onSuccess = { connection ->
                    _uiState.update {
                        it.copy(
                            isConnecting = false,
                            integration = it.integration ?: connection,
                            message = UiText.Resource(Res.string.pos_connected, connection.merchantCode)
                        )
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isConnecting = false, message = e.toUiText()) } }
            )
        }
    }

    fun syncNow() {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (!state.canEdit || state.isSyncing || state.integration == null) return
        _uiState.update { it.copy(isSyncing = true) }
        viewModelScope.launch {
            posIntegrations.syncNow(workplaceId).fold(
                onSuccess = { summary ->
                    _uiState.update { it.copy(isSyncing = false, message = UiText.Resource(Res.string.pos_synced, summary.written)) }
                },
                onFailure = { e -> _uiState.update { it.copy(isSyncing = false, message = e.toUiText()) } }
            )
        }
    }

    /** Optimistic: the connection goes at once and comes back if the function refuses. */
    fun disconnect() {
        val before = _uiState.value
        val workplaceId = before.workplaceId ?: return
        if (!before.canEdit || before.integration == null) return
        _uiState.update { it.copy(integration = null, isDisconnecting = true) }
        viewModelScope.launch {
            posIntegrations.disconnect(workplaceId).fold(
                onSuccess = { _uiState.update { it.copy(isDisconnecting = false, message = UiText.Resource(Res.string.pos_disconnected)) } },
                onFailure = { e -> _uiState.update { it.copy(isDisconnecting = false, integration = before.integration, message = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }
}
